import type {SseEventUnsubscriber} from 'shared/realtime';
import type {AsyncProcessStatus, Defined, Identified, NotNull, UUID} from 'src/typings';
import {Subscription} from 'rxjs';

export function getProperty<OBJECT extends Record<string, any>, KEY extends keyof OBJECT>(propertyName: KEY): (object: OBJECT) => OBJECT[KEY] {
    return (object: OBJECT) => object[propertyName];
}

export function getId<T extends Identified<ID>, ID>(): (object: T) => ID {
    return getProperty('id');
}

/**
 * @module custom-utils
 * @name identifiedValuesEquals
 * @kind function
 *
 * @param {object} iv1 Identified value 1
 * @param {object} iv2 Identified value 2
 *
 * @return {boolean}
 *
 * @description
 * Compares two identified values
 */
export function identifiedValuesEquals(iv1, iv2) {
    if (isUndefined(iv1) || iv1 === null || isUndefined(iv2) || iv2 === null) {
        return false;
    }

    if (iv1 === iv2) {
        return true;
    }

    let val1 = iv1;
    if (isDefined(iv1.id)) {
        val1 = iv1.id;
    }

    let val2 = iv2;
    if (isDefined(iv2.id)) {
        val2 = iv2.id;
    }

    return val1 === val2;
}

/**
 * @module custom-utils
 * @name hash
 * @kind function
 *
 * @param {string} s input
 * @returns {string} computed hash
 *
 * @description
 * Compute simple hash
 */
export function hash(s: string) {
    /* Simple hash function. */
    let a = 1;
    let c = 0;
    let h: number;
    let o: number;

    if (s) {
        a = 0;
        /* eslint-disable no-bitwise */
        for (h = s.length - 1; h >= 0; h--) {
            o = s.charCodeAt(h);
            a = (a << 6 & 268435455) + o + (o << 14);
            c = a & 266338304;
            a = c !== 0 ? a ^ c >> 21 : a;
        }
        /* eslint-enable no-bitwise */
    }
    return String(a);
}

export interface Thenable<T> {
    then: (...args: any[]) => T;
}

/**
 * @module custom-utils
 * @name isThenable
 * @kind function
 *
 * @param value Tested value
 * @returns {boolean} True -> Is thenable object (= have function `then`)
 *
 * @description
 * Checks if value is thenable
 */
export function isThenable<T>(value): value is Thenable<T> {
    return value && typeof value.then === 'function';
}

/**
 * @module custom-utils
 * @name isPromise
 * @kind function
 *
 * @param value Tested value
 * @returns {boolean} True -> Is Promise
 *
 * @description
 * Checks if value is Promise (= has `then` and `catch` functions).
 */
export function isPromise<T>(value): value is Promise<T> {
    return value &&
        typeof value.then === 'function' &&
        typeof value.catch === 'function';
}

/**
 * @module custom-utils
 * @name uuid
 * @kind function
 *
 * @returns {UUID} UUID
 *
 * @description
 * Returns UUID v4
 */
export function uuid(): UUID {
    // !!! crypto.randomUUID is available only in secure context !!!
    if (typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID() as UUID;
    } else {
        return pseudoRandomUuid(); // fallback for unit testing
    }
}

/**
 * @module custom-utils
 * @name isValidUuidString
 * @kind function
 *
 * @param {string} value tested value
 * @returns {boolean} true if value is valid UUID string
 */
export function isValidUuidString(value: string): boolean {
    if (isNullOrUndefined(value)) {
        return false;
    }
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
    return uuidRegex.test(value);
}

function pseudoRandomUuid(): UUID {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // eslint-disable-next-line
        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    }) as UUID;
}

/**
 * @module custom-utils
 * @name isFunction
 * @kind function
 *
 * @param functionToCheck
 *
 * @returns {functionToCheck is (...args: any[]) => any}
 *
 * @description
 * Checks if argument is function.
 */
export function isFunction(functionToCheck): functionToCheck is (...args: any[]) => any {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
}

export function isNull<T>(value: T): value is null {
    return value === null;
}

export function isUndefined<T>(value: T): value is undefined {
    return typeof value === 'undefined';
}

export function isNullOrUndefined<T>(value: T): value is null | undefined {
    return isNull(value) || isUndefined(value);
}

export function isDefined<T>(value: T): value is Defined<T> {
    return typeof value !== 'undefined';
}

export function isNotNull<T>(value: T): value is NotNull<T> {
    return value !== null;
}

export function exists<T>(value: T): value is NonNullable<T> {
    return !isNull(value) && !isUndefined(value);
}

export function assertExists<T>(value: T): asserts value is NonNullable<T> {
    if (isNullOrUndefined(value)) {
        throw new TypeError('Value is nullable');
    }
}

export function assertIsDefined<T>(value: T): asserts value is NonNullable<T> {
    if (isUndefined(value)) {
        throw new TypeError('Value is undefined');
    }
}

// https://dev.to/ddiprose/exhaustive-switch-statement-with-typescript-26dh
export function assertUnreachable(value: never, errorMessage?: string): never {
    throw new Error(errorMessage ?? `ERROR! [Exhaustive switch] Reached forbidden guard function with unexpected value: ${JSON.stringify(value)}`);
}

export function isAsyncProcessStopped(asyncProcessStatus: AsyncProcessStatus<any>) {
    return ['FAILED', 'CANCELLED', 'FINISHED'].includes(asyncProcessStatus.state);
}

export function isAsyncProcessRunning(asyncProcessStatus: AsyncProcessStatus<any>) {
    return asyncProcessStatus.state === 'RUNNING';
}

export function hasAsyncProcessFailed(asyncProcessStatus: AsyncProcessStatus<any>) {
    return asyncProcessStatus.state === 'FAILED';
}

export function wasAsyncProcessCancelled(asyncProcessStatus: AsyncProcessStatus<any>) {
    return asyncProcessStatus.state === 'CANCELLED';
}

export function hasAsyncProcessFinishedSuccessfully(asyncProcessStatus: AsyncProcessStatus<any>) {
    return asyncProcessStatus.state === 'FINISHED';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ignoreUnusedProperties(...args: any[]) {
    // do nothing - this is just to silence warnings from eslint, svelte-check and others
}

export function isTouchDevice(): boolean {
    return matchMedia('(hover: none)').matches;
}

export function isAppleDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase() || '';

    // Check for iOS devices: iPhone, iPad, iPod
    const isIos = /iphone|ipad|ipod/.test(userAgent) ||
        /iphone|ipad|ipod/.test(platform) ||
        // Check for iPad on iOS 13+ which reports as Mac
        (platform === 'macintel' && navigator.maxTouchPoints > 1);

    // Check for Mac devices
    const isMac = /mac/.test(platform) || /macintosh/.test(userAgent);

    return isIos || isMac;
}

export function isIosDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase() || '';

    // Check for iOS devices: iPhone, iPad, iPod
    return /iphone|ipad|ipod/.test(userAgent) ||
        /iphone|ipad|ipod/.test(platform) ||
        // Check for iPad on iOS 13+ which reports as Mac
        (platform === 'macintel' && navigator.maxTouchPoints > 1);
}

export function isAndroidDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
}

export function assertTrue(expression: boolean, message = 'Failed assert, expression is false'): void {
    if (!expression) {
        throw new Error(message);
    }
}

type CleanupCapableResource =
    Subscription
    | SseEventUnsubscriber
    | AbortController
    | { unsubscribe: () => void }
    | (() => void)
    | undefined;

export function cleanup(...resources: CleanupCapableResource[]) {
    resources.forEach((resource) => {
        if (isNullOrUndefined(resource)) {
            return;
        }

        if (resource instanceof Subscription || 'unsubscribe' in resource && isFunction(resource.unsubscribe)) {
            resource.unsubscribe();
        }

        if (resource instanceof AbortController) {
            resource.abort();
        }

        if (isFunction(resource)) {
            resource();
        }
    });
}